import { useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, ChevronLeft, ChevronRight, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { MOV_TYPE, fmtDateTime, fmtQty, fmtCHF } from "@/lib/format";
import { MapBadge } from "@/components/StatusBadge";
import { Card, Empty, PageHeader, Sel, opts, mapOpts } from "@/components/Field";
import { MovementDialog } from "@/components/dialogs/MovementDialog";
import { MultiExitDialog } from "@/components/dialogs/MultiExitDialog";

export default function MovementsPage() {
  const { assoc, can } = useAuth();
  const [f, setF] = useState({ type: "", reason: "", product_id: "", event_id: "", user_id: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(null);
  const set = (k) => (v) => { setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v })); setPage(1); };
  const qs = new URLSearchParams(Object.fromEntries(Object.entries({ ...f, page, page_size: 25 }).filter(([, v]) => v)));
  const { data, reload } = useFetch(`/movements?${qs}`);
  const products = useFetch("/products?page_size=200&status=").data?.items || [];
  const events = useFetch("/events").data || [];
  const members = useFetch("/members").data || [];
  const pages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;

  return (
    <>
      <PageHeader title="Entrate e uscite" subtitle="Storico completo dei movimenti di magazzino">
        {can("movements", "create") && <>
          <Button data-testid="movements-new-entry" className="h-11" onClick={() => setDialog("in")}><ArrowDownToLine className="h-4 w-4 mr-1" /> Entrata</Button>
          <Button data-testid="movements-new-exit" className="h-11" onClick={() => setDialog("out")}><ArrowUpFromLine className="h-4 w-4 mr-1" /> Uscita</Button>
          <Button data-testid="movements-new-multi-exit" variant="outline" className="h-11" onClick={() => setDialog("multi")}><Layers className="h-4 w-4 mr-1" /> Uscita multipla</Button>
        </>}
      </PageHeader>
      <Card className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
        <Sel testId="movements-filter-type" value={f.type} onChange={set("type")} options={mapOpts(MOV_TYPE)} placeholder="Tutti i tipi" />
        <Sel testId="movements-filter-reason" value={f.reason} onChange={set("reason")} options={opts(assoc?.exit_reasons)} placeholder="Tutti i motivi" />
        <Sel testId="movements-filter-product" value={f.product_id} onChange={set("product_id")} options={products.map((p) => ({ value: p.id, label: p.name }))} placeholder="Tutti i prodotti" />
        <Sel testId="movements-filter-event" value={f.event_id} onChange={set("event_id")} options={events.map((e) => ({ value: e.id, label: e.name }))} placeholder="Tutti gli eventi" />
        <Sel testId="movements-filter-user" value={f.user_id} onChange={set("user_id")} options={members.map((m) => ({ value: m.user_id, label: m.name }))} placeholder="Tutti gli utenti" />
        <Input data-testid="movements-filter-from" type="date" value={f.date_from} onChange={set("date_from")} className="h-11" aria-label="Dal" />
        <Input data-testid="movements-filter-to" type="date" value={f.date_to} onChange={set("date_to")} className="h-11" aria-label="Al" />
      </Card>
      {data && data.items.length === 0 ? <Empty testId="movements-empty" text="Nessun movimento trovato." /> : (
        <Card>
          <ul className="divide-y" data-testid="movements-list">
            {data?.items.map((m) => (
              <li key={m.id} data-testid={`movement-row-${m.id}`} className="flex items-center justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-slate-900">{m.product_name} · {m.type === "adjust" && m.quantity > 0 ? "+" : ""}{fmtQty(m.quantity)} {m.unit}</div>
                  <div className="text-sm text-slate-500">
                    {fmtDateTime(m.occurred_at)} · registrato da {m.recorded_by_name}
                    {m.picked_up_by && m.picked_up_by !== m.recorded_by_name && ` · ritirato da ${m.picked_up_by}`}
                    {m.recipient && ` · per ${m.recipient}`}{m.reason && ` · ${m.reason}`}{m.event_name && ` · ${m.event_name}`}{m.lot_number && ` · lotto ${m.lot_number}`}{m.total_cost != null && ` · costo ${fmtCHF(m.total_cost)}`}{m.invoice_number && ` · fattura ${m.invoice_number}`}
                  </div>
                </div>
                <MapBadge map={MOV_TYPE} value={m.type} />
              </li>
            ))}
          </ul>
        </Card>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <Button variant="outline" className="h-11" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="movements-prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm">Pagina {page} di {pages}</span>
          <Button variant="outline" className="h-11" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="movements-next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      )}
      <MovementDialog open={dialog === "in"} mode="in" onOpenChange={(o) => !o && setDialog(null)} onSaved={reload} />
      <MovementDialog open={dialog === "out"} mode="out" onOpenChange={(o) => !o && setDialog(null)} onSaved={reload} />
      <MultiExitDialog open={dialog === "multi"} onOpenChange={(o) => !o && setDialog(null)} onSaved={reload} />
    </>
  );
}
